import React, { createContext, useContext, useRef } from 'react';
import { getMarketConfig } from '../config/markets';
import { setDefaultBaseId } from '../services/airtable';

// Create the context
const MarketContext = createContext(null);

/**
 * MarketProvider - Wraps the app and provides market config to all components
 *
 * Two modes:
 * - Env mode (TCC): config prop is omitted, reads from REACT_APP_MARKET env var
 * - Path mode (OTC): config prop is passed from MarketLayout based on URL slug
 *
 * IMPORTANT: setDefaultBaseId is called synchronously during render (not in
 * useEffect) so that child components' useEffects always see the correct base
 * ID when they fire on mount.  React fires child effects before parent effects,
 * so a useEffect here would be too late.
 */
export const MarketProvider = ({ config: configProp, children }) => {
  const config = configProp || getMarketConfig();
  const prevBaseIdRef = useRef(null);

  // Synchronously set the Airtable base ID during render so it's ready
  // before any child useEffects fire.  The ref guard avoids redundant calls.
  if (config?.airtableBaseId && config.airtableBaseId !== prevBaseIdRef.current) {
    setDefaultBaseId(config.airtableBaseId);
    prevBaseIdRef.current = config.airtableBaseId;
  }

  return (
    <MarketContext.Provider value={config}>
      {children}
    </MarketContext.Provider>
  );
};

/**
 * useMarket - Hook to access market configuration
 *
 * Usage:
 *   const market = useMarket();
 *   console.log(market.name);        // "Tri-Cities Central"
 *   console.log(market.cities);      // ["St. Charles", "Geneva", "Batavia"]
 *   console.log(market.theme.primaryColor);  // "#2c5530"
 */
export const useMarket = () => {
  const context = useContext(MarketContext);

  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }

  return context;
};

export default MarketContext;
